import Settings16 from '@carbon/icons-react/es/settings/16';
import { useBoolean } from 'ahooks';
import { Dropdown, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import isEmpty from 'lodash/isEmpty';
import React, { FC, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import {
  ProjectTemplateData,
  updateProjectTemplate,
  useActiveProjectTemplate,
  useTransformTabularData,
} from 'data/templates/projects';
import { useProjectDynamicTemplateConditions } from 'data/templates/projects/hooks/use-project-dynamic-template';
import { useTranslate } from 'hooks/use-translation';
import { ProjectTemplateToolBar } from '../commons';
import { AutoCompressModal } from '../commons/auto-compress-modal';
import { rowClassName, useTableActions } from '../plan/table/actions';
import { EditableCell } from '../plan/table/cell';
import { CustomExpandIcon } from '../plan/table/collapse-icon';
import { useProjectTemplateColumns } from '../plan/table/columns';
import { BodyRow, HeaderRow } from '../plan/table/row';

const components = {
  body: {
    row: BodyRow,
    cell: EditableCell,
  },
  header: {
    row: HeaderRow,
  },
};

const BorderForTable = styled.div`
  border-left: 1px solid var(--border-color);
`;
const TableWrapper = styled.div`
  height: calc(100vh - 170px);
  margin-left: var(--layout-02);
  float: left;
  width: calc(100% - calc(1 * var(--layout-02)));
  position: relative;

  ${BorderForTable} {
    position: absolute;
    height: 100%;
    top: 0;
    left: 374px;
  }

  .ant-table-wrapper {
    max-width: initial;
  }

  .ant-table-cell-fix-left {
    left: 0 !important;
  }

  .ant-table-body {
    overflow: auto !important;
    /* overflow-x: hidden !important; */
    max-height: calc(
      100vh - 210px
    ) !important; /*  To leave space for template Header  */
    padding-bottom: var(--spacing-02);
  }

  .more__actions {
    display: none;
  }

  .ant-table-row-level-0 {
    font-weight: inherit;

    td:first-child {
      font-weight: var(--font-weight-bold);
    }
  }

  .ant-table table {
    min-width: 100% !important;
    table-layout: inherit !important;
  }

  .dynamic-conditions-column {
    padding: 0 !important;
  }

  td.dynamic-conditions-column {
    border-left: 1px solid var(--border-color);
  }

  th.dynamic-conditions-column {
    border-left: 1px solid var(--border-color);
  }

  .dynamic-template-row {
    cursor: default;
  }
`;

const ConfigItem = styled.div`
  display: flex;
  gap: var(--spacing-03);
  align-items: center;
`;

export const ProjectTemplatePlanList: FC<{
  additionalColumns?: any[];
}> = ({ additionalColumns }) => {
  const history = useHistory();
  const { url } = useRouteMatch();
  const dispatch = useDispatch();
  const dynamicConditions = useProjectDynamicTemplateConditions();
  const template = useActiveProjectTemplate();
  const [enableAutoCompressModal, { setTrue, setFalse }] = useBoolean(false);
  const isAutoCompressTurnedOn =
    // while sending payload we send it outside templatemeta, but when we receive we get it in templateMeta
    template.compressTimeline ??
    template.templateMeta?.compressTimeline ??
    false;
  const translate = useTranslate();
  const menuItemLabel = translate(
    'templates.dynamic_conditions.compress_timeline_menu_item',
    {
      action: isAutoCompressTurnedOn ? 'off' : 'on',
    },
  );

  const columns = useProjectTemplateColumns();
  const {
    expandedRowKeys,
    hasClickedExpandCollapse,
    onExpand,
    mode,
    expandCollapse,
  } = useTableActions();

  const _columns = useMemo<ColumnsType<ProjectTemplateData>>(() => {
    let result = columns.map((column) => ({
      ...column,
      fixed: true,
      editable: false,
      width: column.key === 'duration' ? '89px' : column.width,
      onCell: () => {},
    })) as unknown as ColumnsType<ProjectTemplateData>;

    if (additionalColumns) {
      result = [...result, ...additionalColumns];
    }

    return result;
  }, [additionalColumns, columns]);

  const _data = useTransformTabularData();

  const data = useMemo(
    () =>
      _data.map((_obj) => {
        return {
          ..._obj,
          children: _obj.children?.filter((_child) => !_child.data.isNew),
        };
      }),
    [_data],
  );

  const toggleAutoCompression = useCallback(() => {
    dispatch(
      updateProjectTemplate({
        ...template,
        compressTimeline: template.templateMeta?.compressTimeline
          ? !template.templateMeta?.compressTimeline
          : true,
      }),
    );
    setFalse();
  }, [dispatch, setFalse, template]);

  const menu = () => (
    <Menu multiple={false}>
      <Menu.Item onClick={setTrue}>{menuItemLabel}</Menu.Item>
    </Menu>
  );

  return (
    <>
      <ProjectTemplateToolBar
        data={data}
        mode={mode}
        expandCollapse={expandCollapse}
        secondary={
          !isEmpty(dynamicConditions) ? (
            <ConfigItem>
              <Button
                size="small"
                type="link"
                onClick={() => history.push(`${url}/preview`)}
              >
                <FormattedMessage id="templates.dynamic.conditions.preview.link" />
              </Button>
              <Dropdown overlay={menu} trigger={['hover']}>
                <Button icon={<Settings16 />} type="text" />
              </Dropdown>
            </ConfigItem>
          ) : undefined
        }
      />
      <TableWrapper>
        <Table
          columns={_columns}
          dataSource={data}
          pagination={false}
          size="small"
          sticky
          scroll={{ x: true, y: 'calc(100vh - 240px)' }}
          expandIcon={CustomExpandIcon}
          components={components}
          expandable={{
            expandedRowKeys: hasClickedExpandCollapse
              ? expandedRowKeys
              : undefined,
            defaultExpandAllRows: true,
            indentSize: 24,
            onExpand,
          }}
          rowClassName={(record) =>
            `${rowClassName(record)} dynamic-template-row`
          }
        />
      </TableWrapper>
      <AutoCompressModal
        isVisible={enableAutoCompressModal}
        isAutoCompressTurnedOn={isAutoCompressTurnedOn}
        closeModal={setFalse}
        toggleAutoCompression={toggleAutoCompression}
      />
    </>
  );
};
